import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  DialogContentText,
  Box,
  Card,
  Chip,
  Stack,
  Typography,
  Divider,
  Slider,
  Grid,
  ClickAwayListener,
  Fade,
  Popover,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SortIcon from "@mui/icons-material/Sort";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import GoalsTimeline from "./goals-legacy/GoalsTimeline";
import { calculateMonthlyAmount, validateGoal } from "./goals-legacy/goalUtils";
import GoalFeasibilityIndicator from "./goals-legacy/GoalFeasibilityIndicator";
import IntegrationPrompts from "./goals-legacy/IntegrationPrompts";
import ModernGoalWizard from "./goals-legacy/ModernGoalWizard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../utils/apiConfig";
import LifetimeFinancialAnalysis from "./goals-legacy/LifetimeFinancialAnalysis";
import KeyTakeaways from "./goals-legacy/KeyTakeaways";

import logger from '../../../../utils/logger';

const GoalsTable = ({ goals: initialGoals, onToggle }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [goals, setGoals] = useState([]);

  // Update local goals when initialGoals changes
  useEffect(() => {
    if (Array.isArray(initialGoals)) {
      setGoals(
        initialGoals.map((goal) => ({
          ...goal,
          id: goal.goal_id || goal.id, // Handle both API and local IDs
          progress: goal.progress || 0,
          achievabilityScore: goal.achievabilityScore || 100,
        })),
      );
    }
  }, [initialGoals]);

  const [formData, setFormData] = useState({
    name: "",
    member: "",
    amount: "",
    targetDate: "",
    priority: 1,
    category: "",
    frequency: "one-time",
  });

  const [inlineEdit, setInlineEdit] = useState({
    goalId: null,
    field: null,
    value: "",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const frequencies = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annual", label: "Annual" },
    { value: "one-time", label: "One-time" },
  ];

  const [financialData, setFinancialData] = useState({
    netIncome: 10000, // Example value - replace with actual data
    expenses: 7000, // Example value - replace with actual data
  });

  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const handleOpenModal = (goal = null) => {
    if (goal) {
      setFormData({
        name: goal.name,
        member: goal.member,
        amount: goal.amount,
        targetDate: goal.targetDate,
        priority: goal.priority,
        category: goal.category,
        frequency: goal.frequency,
      });
      setSelectedGoal(goal);
    } else {
      setFormData({
        name: "",
        member: "",
        amount: "",
        targetDate: "",
        priority: 1,
        category: "",
        frequency: "one-time",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedGoal(null);
  };

  const handleDeleteClick = (goal) => {
    setSelectedGoal(goal);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    setGoals(goals.filter((g) => g.id !== selectedGoal.id));
    setOpenDeleteDialog(false);
    setSelectedGoal(null);
  };

  const handleSaveGoal = () => {
    const monthlyAmount = calculateMonthlyAmount(formData);
    const currentMonthlyGoals = goals.reduce(
      (sum, goal) =>
        sum + (selectedGoal?.id === goal.id ? 0 : calculateMonthlyAmount(goal)),
      0,
    );

    if (
      monthlyAmount + currentMonthlyGoals >
      financialData.netIncome - financialData.expenses
    ) {
      setWarningMessage(
        `This goal requires $${monthlyAmount.toLocaleString()} monthly, but you only have ` +
          `$${(financialData.netIncome - financialData.expenses - currentMonthlyGoals).toLocaleString()} ` +
          `available after other goals. Would you like to proceed anyway?`,
      );
      setOpenWarningDialog(true);
      return;
    }

    saveGoal();
  };

  const saveGoal = () => {
    if (selectedGoal) {
      setGoals(
        goals.map((g) =>
          g.id === selectedGoal.id ? { ...g, ...formData } : g,
        ),
      );
    } else {
      setGoals([
        ...goals,
        {
          id: goals.length + 1,
          ...formData,
          progress: 0,
          achievabilityScore: 100,
        },
      ]);
    }
    handleCloseModal();
  };

  // Add sortable columns configuration
  const sortableColumns = [
    { key: "name", label: "Goal Name" },
    { key: "member", label: "Member" },
    { key: "amount", label: "Target Amount" },
    { key: "targetDate", label: "Target Date" },
    { key: "priority", label: "Priority" },
    { key: "progress", label: "Progress" },
    { key: "feasibility", label: "Feasibility" },
  ];

  // Enhanced sort function
  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Sort goals
  const sortedGoals = useMemo(() => {
    if (!Array.isArray(goals)) return [];

    const sorted = [...goals];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Convert dates for comparison
        if (sortConfig.key === "targetDate") {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sorted;
  }, [goals, sortConfig]);

  // Add inline editing for target date
  const handleDateEdit = (goalId, currentDate) => {
    setInlineEdit({
      goalId,
      field: "targetDate",
      value: currentDate,
    });
  };

  // Handle inline editing
  const handleInlineEdit = (goalId, field, value, currentGoal) => {
    // Validate before allowing edit
    const testData = { ...currentGoal, [field]: value };
    const errors = validateGoal(testData);

    if (errors[field]) {
      // Show error tooltip
      return;
    }

    setInlineEdit({ goalId, field, value });
  };

  const handleInlineEditSave = () => {
    if (!inlineEdit.goalId) return;

    setGoals(
      goals.map((goal) => {
        if (goal.id === inlineEdit.goalId) {
          return { ...goal, [inlineEdit.field]: inlineEdit.value };
        }
        return goal;
      }),
    );
    setInlineEdit({ goalId: null, field: null, value: "" });
  };

  // Handle row menu
  const handleRowMenuOpen = (event, goal) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(goal);
  };

  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  // Duplicate goal function
  const handleDuplicateGoal = (goal) => {
    const newGoal = {
      ...goal,
      id: goals.length + 1,
      name: `${goal.name} (Copy)`,
      progress: 0,
    };
    setGoals([...goals, newGoal]);
    handleRowMenuClose();
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(goals);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setGoals(items);
  };

  // Add the categorizeGoal function
  const categorizeGoal = (name) => {
    const lowercase = name.toLowerCase();
    if (lowercase.includes("retire")) return "Retirement";
    if (lowercase.includes("car") || lowercase.includes("vehicle"))
      return "Major Purchase";
    if (lowercase.includes("debt") || lowercase.includes("loan"))
      return "Debt Reduction";
    if (lowercase.includes("save") || lowercase.includes("savings"))
      return "Savings";
    if (lowercase.includes("education") || lowercase.includes("college"))
      return "Education";
    if (lowercase.includes("house") || lowercase.includes("home"))
      return "Housing";
    if (lowercase.includes("business")) return "Business";
    if (lowercase.includes("tax")) return "Tax Planning";
    if (lowercase.includes("estate")) return "Estate Planning";
    if (lowercase.includes("insurance")) return "Insurance";
    return "Other";
  };

  if (!Array.isArray(goals) || goals.length === 0) {
    return (
      <Card elevation={2} className="p-6 rounded-xl">
        <Typography variant="h5" component="h2" className="font-semibold mb-4">
          Financial Goals
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          className="text-center py-8"
        >
          No goals found. Click the "Add New Goal" button to create your first
          goal.
        </Typography>
      </Card>
    );
  }

  return (
    <>
      <Card elevation={2} className="p-6 rounded-xl">
        <Stack spacing={3}>
          <Box className="flex justify-between items-center">
            <Typography variant="h5" component="h2" className="font-semibold">
              Financial Goals
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              className="bg-blue-600"
            >
              Add Goal
            </Button>
          </Box>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="goals">
              {(provided) => (
                <TableContainer
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  <Table>
                    <TableHead>
                      <TableRow className="bg-gray-50">
                        {sortableColumns.map((column) => (
                          <TableCell
                            key={column.key}
                            onClick={() => handleSort(column.key)}
                            className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            align={column.key === "amount" ? "right" : "left"}
                          >
                            <Box className="flex items-center">
                              {column.label}
                              {sortConfig.key === column.key && (
                                <SortIcon
                                  className={`ml-1 transition-transform ${
                                    sortConfig.direction === "desc"
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                  fontSize="small"
                                />
                              )}
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedGoals.map((goal, index) => (
                        <Draggable
                          key={goal.id.toString()}
                          draggableId={goal.id.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`
                                hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150
                                ${snapshot.isDragging ? "bg-blue-50" : ""}
                              `}
                            >
                              <TableCell>
                                {inlineEdit.goalId === goal.id &&
                                inlineEdit.field === "name" ? (
                                  <ClickAwayListener
                                    onClickAway={handleInlineEditSave}
                                  >
                                    <TextField
                                      autoFocus
                                      size="small"
                                      value={inlineEdit.value}
                                      onChange={(e) =>
                                        setInlineEdit({
                                          ...inlineEdit,
                                          value: e.target.value,
                                        })
                                      }
                                      onKeyPress={(e) => {
                                        if (e.key === "Enter")
                                          handleInlineEditSave();
                                      }}
                                      InputProps={{
                                        endAdornment: (
                                          <Box className="space-x-1">
                                            <IconButton
                                              size="small"
                                              onClick={handleInlineEditSave}
                                            >
                                              <CheckIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                setInlineEdit({
                                                  goalId: null,
                                                  field: null,
                                                  value: "",
                                                })
                                              }
                                            >
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        ),
                                      }}
                                    />
                                  </ClickAwayListener>
                                ) : (
                                  <Box
                                    className="flex flex-col cursor-pointer"
                                    onClick={() =>
                                      handleInlineEdit(
                                        goal.id,
                                        "name",
                                        goal.name,
                                        goal,
                                      )
                                    }
                                  >
                                    <Typography variant="body1">
                                      {goal.name}
                                    </Typography>
                                    <Chip
                                      label={categorizeGoal(goal.name)}
                                      size="small"
                                      className="mt-1 w-fit"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip label={goal.member} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                {inlineEdit.goalId === goal.id &&
                                inlineEdit.field === "amount" ? (
                                  <ClickAwayListener
                                    onClickAway={handleInlineEditSave}
                                  >
                                    <TextField
                                      autoFocus
                                      size="small"
                                      type="number"
                                      value={inlineEdit.value}
                                      onChange={(e) =>
                                        setInlineEdit({
                                          ...inlineEdit,
                                          value: e.target.value,
                                        })
                                      }
                                      error={Boolean(
                                        validateGoal({
                                          ...goal,
                                          amount: inlineEdit.value,
                                        }).amount,
                                      )}
                                      helperText={
                                        validateGoal({
                                          ...goal,
                                          amount: inlineEdit.value,
                                        }).amount
                                      }
                                    />
                                  </ClickAwayListener>
                                ) : (
                                  <Box
                                    className="cursor-pointer"
                                    onClick={() =>
                                      handleInlineEdit(
                                        goal.id,
                                        "amount",
                                        goal.amount.toString(),
                                        goal,
                                      )
                                    }
                                  >
                                    ${goal.amount.toLocaleString()}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                {inlineEdit.goalId === goal.id &&
                                inlineEdit.field === "targetDate" ? (
                                  <ClickAwayListener
                                    onClickAway={handleInlineEditSave}
                                  >
                                    <TextField
                                      type="date"
                                      size="small"
                                      value={inlineEdit.value}
                                      onChange={(e) =>
                                        setInlineEdit({
                                          ...inlineEdit,
                                          value: e.target.value,
                                        })
                                      }
                                      InputProps={{
                                        className: "w-40",
                                      }}
                                    />
                                  </ClickAwayListener>
                                ) : (
                                  <Box
                                    className="cursor-pointer"
                                    onClick={() =>
                                      handleDateEdit(goal.id, goal.targetDate)
                                    }
                                  >
                                    {new Date(
                                      goal.targetDate,
                                    ).toLocaleDateString()}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={
                                    goal.priority >= 4 ? (
                                      <PriorityHighIcon />
                                    ) : undefined
                                  }
                                  label={goal.priority}
                                  size="small"
                                  className={`
                                    ${
                                      goal.priority >= 4
                                        ? "bg-red-100 text-red-800"
                                        : goal.priority >= 3
                                          ? "bg-yellow-100 text-yellow-800"
                                          : "bg-gray-100 text-gray-800 dark:text-gray-200"
                                    }
                                  `}
                                />
                              </TableCell>
                              <TableCell>
                                <Box className="w-full">
                                  {inlineEdit.goalId === goal.id &&
                                  inlineEdit.field === "progress" ? (
                                    <ClickAwayListener
                                      onClickAway={handleInlineEditSave}
                                    >
                                      <Slider
                                        value={parseInt(inlineEdit.value)}
                                        onChange={(_, value) =>
                                          setInlineEdit({
                                            ...inlineEdit,
                                            value: value.toString(),
                                          })
                                        }
                                        valueLabelDisplay="auto"
                                        step={5}
                                        marks
                                        min={0}
                                        max={100}
                                      />
                                    </ClickAwayListener>
                                  ) : (
                                    <Box
                                      className="cursor-pointer"
                                      onClick={() =>
                                        handleInlineEdit(
                                          goal.id,
                                          "progress",
                                          goal.progress.toString(),
                                          goal,
                                        )
                                      }
                                    >
                                      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full ${
                                            goal.progress >= 75
                                              ? "bg-green-600"
                                              : goal.progress >= 50
                                                ? "bg-green-400"
                                                : goal.progress >= 25
                                                  ? "bg-yellow-400"
                                                  : "bg-red-400"
                                          }`}
                                          style={{ width: `${goal.progress}%` }}
                                        />
                                      </div>
                                      <Typography
                                        variant="caption"
                                        className="mt-1 text-gray-600 dark:text-gray-400"
                                      >
                                        {goal.progress}%
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <GoalFeasibilityIndicator
                                  goal={goal}
                                  monthlySurplus={
                                    financialData.netIncome -
                                    financialData.expenses
                                  }
                                  otherGoalsTotal={goals.reduce(
                                    (sum, g) =>
                                      g.id !== goal.id
                                        ? sum + calculateMonthlyAmount(g)
                                        : sum,
                                    0,
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRowMenuOpen(e, goal)}
                                  className="text-gray-500 dark:text-gray-500"
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Droppable>
          </DragDropContext>

          {/* Row Actions Menu */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleRowMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box className="py-1">
              <MenuItem
                onClick={() => {
                  handleOpenModal(selectedRow);
                  handleRowMenuClose();
                }}
              >
                <EditIcon fontSize="small" className="mr-2" /> Edit Goal
              </MenuItem>
              <MenuItem onClick={() => handleDuplicateGoal(selectedRow)}>
                <ContentCopyIcon fontSize="small" className="mr-2" /> Duplicate
                Goal
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleDeleteClick(selectedRow);
                  handleRowMenuClose();
                }}
                className="text-red-600"
              >
                <DeleteIcon fontSize="small" className="mr-2" /> Delete Goal
              </MenuItem>
            </Box>
          </Popover>

          {/* Improved Add/Edit Modal */}
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle className="flex justify-between items-center">
              {selectedGoal ? "Edit Goal" : "Add New Goal"}
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} className="mt-0">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Goal Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Member</InputLabel>
                    <Select
                      value={formData.member}
                      onChange={(e) =>
                        setFormData({ ...formData, member: e.target.value })
                      }
                      label="Member"
                    >
                      <MenuItem value="Household">Household</MenuItem>
                      <MenuItem value="John">John</MenuItem>
                      <MenuItem value="Sarah">Sarah</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Target Amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Target Date"
                    type="date"
                    value={formData.targetDate}
                    onChange={(e) =>
                      setFormData({ ...formData, targetDate: e.target.value })
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={formData.frequency}
                      onChange={(e) =>
                        setFormData({ ...formData, frequency: e.target.value })
                      }
                      label="Frequency"
                    >
                      {frequencies.map((freq) => (
                        <MenuItem key={freq.value} value={freq.value}>
                          {freq.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value })
                      }
                      label="Priority"
                    >
                      {[1, 2, 3, 4, 5].map((p) => (
                        <MenuItem key={p} value={p}>
                          {p} -{" "}
                          {p === 1 ? "Low" : p === 5 ? "Critical" : "Medium"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className="p-4">
              <Button onClick={handleCloseModal} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleSaveGoal}
                variant="contained"
                className="bg-blue-600"
              >
                {selectedGoal ? "Save Changes" : "Add Goal"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this goal? This action cannot be
                undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={handleConfirmDelete} color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Warning Dialog */}
          <Dialog
            open={openWarningDialog}
            onClose={() => setOpenWarningDialog(false)}
          >
            <DialogTitle>Warning: Goal May Exceed Budget</DialogTitle>
            <DialogContent>
              <DialogContentText>{warningMessage}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenWarningDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setOpenWarningDialog(false);
                  saveGoal();
                }}
                color="warning"
              >
                Proceed Anyway
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Card>
      <IntegrationPrompts />
    </>
  );
};

const PlanningGoals = ({ householdId, userId }) => {
  const [error, setError] = useState(null);
  const leftColumnRef = useRef(null);

  // Fetch existing goals
  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ["goals", householdId, userId],
    queryFn: () =>
      api
        .get(
          `/advisor-portal/client-database/household-profile/${householdId}/goals-legacy`,
        )
        .then((r) => r.data),
    enabled: !!householdId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch projections for timeline
  const {
    data: projections = { timeline: [] },
    isLoading: projectionsLoading,
  } = useQuery({
    queryKey: ["projections", householdId],
    queryFn: () =>
      api
        .get(
          `/advisor-portal/client-database/household-profile/${householdId}/goals-legacy/projections`,
        )
        .then((r) => r.data),
    enabled: !!householdId,
    staleTime: 1000 * 60 * 5,
    retry: false,
    onError: (error) => {
      logger.warn("Projections data not available:", error.message);
    },
  });

  if (goalsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Box className="p-6">
      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Lifetime Financial Analysis */}
      {!projectionsLoading && (
        <LifetimeFinancialAnalysis
          data={projections.timeline}
          goals={goals}
          householdId={householdId}
        />
      )}

      {/* Main Content Grid */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Left Column */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={3} ref={leftColumnRef}>
            {/* Goals Timeline */}
            <Card elevation={2} sx={{ borderRadius: 4, p: 3 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Financial Goals Timeline
              </Typography>
              <GoalsTimeline goals={goals} householdId={householdId} />
            </Card>

            {/* Key Takeaways */}
            <Card elevation={2} sx={{ borderRadius: 4, p: 3 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Key Takeaways
              </Typography>
              <KeyTakeaways goals={goals} />
            </Card>
          </Stack>
        </Grid>

        {/* Right Column - Modern Goal Wizard */}
        <Grid item xs={12} lg={5}>
          <ModernGoalWizard
            householdId={householdId}
            userId={userId}
            leftColumnRef={leftColumnRef}
          />
        </Grid>
      </Grid>

      {/* Integrations Section */}
      <Box sx={{ mt: 3 }}>
        <IntegrationPrompts />
      </Box>
    </Box>
  );
};

export default PlanningGoals;
